import React, { FC, useEffect, useState } from "react";
import { Checkbox, FormControlLabel, Grid, Tab, Tabs } from "@mui/material";
import FormField from "../../../../../components/common/FormField";
import {
  FT3_TO_M3,
  M3_TO_FT3,
  surveyCollectionSummaryFields,
} from "../surveyUtils";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setNewSurveySummaryLocal } from "../../../../../store/reducers/leadSurveySlice";
import {
  checkValidPostcodeAction,
  setCheckValidPostcodeAction,
} from "../../../../../store/reducers/mmymSlice";
import {
  getCountyOptionsByCountry,
  leadCountryOptions,
} from "../../leadAllFields";

const DEFAULT_PROPERTY_TYPES = [
  "Unknown",
  "House",
  "Town House",
  "Mainsonette",
  "Flat",
  "Bungalow",
  "Cottage",
  "Self Store",
  "Commercial",
];

interface ManageCollectionFormProps {
  checkValidator?: any;
  resetValidator?: any;
  error?: any;
  disabledAction?: boolean;
  setCountryCode?: any;
  countryCode?: any;
  inputRefMap?: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
}

const CollectionSummaryForm: FC<ManageCollectionFormProps> = ({
  checkValidator,
  resetValidator,
  error,
  disabledAction,
  setCountryCode,
  countryCode,
  inputRefMap,
}) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const {
    newSurveySummary,
    propertyTypeList,
  } = useSelector((state: any) => state.leadSurvey);

  // Handle input changes
  const onChangeAction = (event: any, valueFormat?: any, index?: number) => {
    const { id, value, checked, type } = event.target;
    const updatedMovingFrom = [...(newSurveySummary?.movingFrom || [])];

    if (typeof index === "number" && index >= 0) {
      if (type === "checkbox") {
        updatedMovingFrom[index] = {
          ...updatedMovingFrom[index],
          [id]: checked,
        };
      } else if (id === "volumeUnit") {
        const prevUnit = updatedMovingFrom[index]?.volumeUnit || "m3";
        const currentVolume = Number(updatedMovingFrom[index]?.volume || 0);

        let convertedVolume = currentVolume;
        if (currentVolume > 0) {
          if (prevUnit === "m3" && value === "ft3")
            convertedVolume = parseFloat(
              (currentVolume * M3_TO_FT3).toFixed(2),
            );
          else if (prevUnit === "ft3" && value === "m3")
            convertedVolume = parseFloat(
              (currentVolume * FT3_TO_M3).toFixed(2),
            );
        }

        updatedMovingFrom[index] = {
          ...updatedMovingFrom[index],
          volumeUnit: value,
          volume: convertedVolume || "",
        };
      } else {
        updatedMovingFrom[index] = {
          ...updatedMovingFrom[index],
          [id]:
            id === "postcode" ? (value ? value.toUpperCase() : value) : value,
        };
      }
    }

    if(id==='access'){
      dispatch(
        setNewSurveySummaryLocal({
          ...newSurveySummary,
          access: value,
        }),
      );
      return;
    };

    dispatch(
      setNewSurveySummaryLocal({
        ...newSurveySummary,
        movingFrom: updatedMovingFrom,
      }),
    );

    resetValidator(id);
  };

  const [postcodeToValidate, setPostcodeToValidate] = useState<{
    index: number;
    postcode: string;
  } | null>(null);

  // Handle blur action
  const onBlurAction = (e: any) => {
    const { id, value } = e.target;
    checkValidator(id);
    if (e.target.id === "postcode" && e.target.value.trim() !== "") {
      setPostcodeToValidate({
        index: activeTab,
        postcode: value.trim(),
      });
    }
  };

  useEffect(() => {
    if (postcodeToValidate) {
      const { postcode } = postcodeToValidate;

      if (postcode.length > 4) {
        dispatch(checkValidPostcodeAction(postcode));
      } else {
        dispatch(setCheckValidPostcodeAction(false));
      }

      setPostcodeToValidate(null);
    }
  }, [postcodeToValidate, dispatch]);
 
  // Use shared function for county options
  const getCountyOptions = getCountyOptionsByCountry;

  const getPropertyTypeOptions = (index: number) => {
    const configuredPropertyTypes = Array.isArray(propertyTypeList)
      ? propertyTypeList
          .map((item: any) => item?.name)
          .filter((name: any) => typeof name === "string" && name.trim() !== "")
      : [];

    const baseOptions =
      configuredPropertyTypes.length > 0
        ? configuredPropertyTypes
        : DEFAULT_PROPERTY_TYPES;

    const savedTypeName = newSurveySummary?.movingFrom?.[index]?.type;
    const hasSavedTypeInOptions =
      typeof savedTypeName === "string" &&
      baseOptions.some(
        (option: string) => option.toLowerCase() === savedTypeName.toLowerCase(),
      );

    if (
      typeof savedTypeName === "string" &&
      savedTypeName.trim() !== "" &&
      !hasSavedTypeInOptions
    ) {
      return [...baseOptions, savedTypeName];
    }

    return baseOptions;
  };

  return (
    <>
      <Grid
        container
        spacing={0.5}
        sx={{
          padding: "0px",
          borderRadius: "5px",
          border: "1px solid #CFCFCF",
          overflow: "hidden",
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          style={{
            paddingLeft: "0px !important",
            background: "#E5F4F1",
            paddingBottom: "6px",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            aria-label="Collection Address Tabs"
            TabIndicatorProps={{ style: { display: "none" } }} // Hide the tab indicator line
            sx={{
              minHeight: "16px",
              "& .MuiTabs-flexContainer": {
                gap: 1, // adds space between tabs (theme.spacing(1))
              },
              "& .MuiTab-root": {
                minHeight: "30px", // affects all Tab children
                // height: "30px",
                padding: "3px 2px 0px 2px",
                textTransform: "capitalize",
                fontWeight: 600,
                // color: "#333333",
                justifyContent: "flex-start",
                alignItems: "center",
              },
            }}
          >
            {newSurveySummary?.movingFrom?.map(
              (address: any, index: number) => (
                <Tab
                  sx={{
                    textAlign: "left",
                    paddingTop: "6px !important",
                    fontWeight: 600,
                    color: "#333333",
                    marginRight: "10px",
                    backgroundColor:
                      activeTab === index ? "#ffffff" : "transparent",
                    borderRadius: "5px",
                    minHeight: "30px !important",
                    paddingLeft: "6px !important",
                    marginTop: "2px",
                    marginBottom: "0px",
                  }}
                  key={index}
                  label={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ marginRight: 4 }}>{`Collection ${
                        index + 1
                      }`}</span>
                    </div>
                  }
                />
              ),
            )}
          </Tabs>
        </Grid>
        {newSurveySummary?.movingFrom?.map((address: any, index: number) => (
          <Grid
            item
            xs={12}
            key={index}
            role="tabpanel"
            hidden={activeTab !== index} // Hide tabs based on the activeTab value
            id={`tab-panel-${index}`}
            aria-labelledby={`tab-${index}`}
            sx={{ background: "#fff", padding: "6px 10px 10px 6px !important" }}
          >
            {activeTab === index && (
              <Grid container spacing={0.5}>
                {surveyCollectionSummaryFields?.map((field, i) => {
                  return (
                    <>
                      {field?.type === "checkbox" ? (
                        <Grid
                          item
                          xs={12}
                          md={field?.md ? field?.md : 1}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            padding: "0px !important",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={disabledAction}
                                checked={
                                  newSurveySummary
                                    ? newSurveySummary?.movingFrom?.[
                                        activeTab
                                      ]?.[field?.id]
                                    : ""
                                }
                                onChange={(e: any) =>
                                  onChangeAction(e, null, activeTab)
                                }
                                name={field?.id}
                                id={field?.id}
                                color="primary"
                              />
                            }
                            label="Verified"
                          />
                        </Grid>
                      ) : (
                        <FormField
                          key={i}
                          inputRef={(el: any) => {
                            if (inputRefMap?.current) {
                              inputRefMap.current[field.id] = el;
                            }
                          }}
                          onPaste={(e: any) =>
                            field?.isPaste ? "" : e.preventDefault()
                          }
                          onKeyDown={(event: any) => {
                            if (
                              event.keyCode === 8 ||
                              event.keyCode === 9 ||
                              event.keyCode === 37 ||
                              event.keyCode === 38 ||
                              event.keyCode === 39 ||
                              event.keyCode === 40 ||
                              event.keyCode === 18 ||
                              event.keyCode === 17 ||
                              event.ctrlKey
                            )
                              return;

                            if (
                              field?.patternAllowed &&
                              !field?.patternAllowed.test(event.key)
                            ) {
                              event.preventDefault();
                            } else if (
                              field?.patternAllowed &&
                              !field?.patternAllowed.test(
                                event.target.value + event.key,
                              )
                            ) {
                              event.preventDefault();
                            }
                          }}
                          {...{
                            ...field,
                            multiline: true,
                            minDate:
                              field.id === "removalDate"
                                ? new Date()
                                : undefined,
                            editReadOnly: disabledAction,
                            inSingleGridForm: true,
                            disabled:
                              field?.id === "removal" ? true : disabledAction,
                            marginTopCountryCode: "2px",
                            GridFieldStyle: {
                              paddingRight:
                                i % 2 === 0 || field?.id === "bedrooms"
                                  ? "4px"
                                  : "0px",
                            },
                            onCountryCodeChange:
                              field.type === "contact" // Example of checking if it's a country code field
                                ? (code: string) => setCountryCode(code)
                                : undefined,
                            ...{
                              ...(field?.type === "contact" && {
                                countryCode: newSurveySummary[field.id]
                                  ? countryCode
                                  : "",
                              }),
                            },
                            value:
                              field?.id === "volumeUnit"
                                ? newSurveySummary?.movingFrom?.[activeTab]
                                    ?.volumeUnit || "m3"
                                : field?.id === "access"
                                ? newSurveySummary?.access || ""
                                : newSurveySummary?.movingFrom?.[activeTab]?.[
                                    field?.id
                                  ] || "",

                            ...{
                              variant: "outlined", // Adding this variant prop
                              options:
                                field.id === "county"
                                  ? getCountyOptions(
                                      newSurveySummary?.movingFrom?.[activeTab]?.country,
                                    )
                                  : field.id === "country"
                                    ? leadCountryOptions
                                    : field.id === "type"
                                      ? getPropertyTypeOptions(activeTab)
                                      : field.options, // Conditional options for county/country/type
                              onChange: (e: any) =>
                                onChangeAction(e, null, activeTab),
                              onBlur: onBlurAction,
                              isNonMarginTop: true,
                              InputProps: {
                                ...(field?.inputProps && field?.inputProps),
                                ...{
                                  disabled:
                                    field?.editReadOnly || disabledAction,
                                },

                                sx:
                                  field?.type === "text" ||
                                  field?.type === "password"
                                    ? {
                                        height: "36px",
                                        padding: "10px 0px 6px 4px",
                                      }
                                    : field?.type === "contact"
                                      ? {
                                          height: "36px",
                                          padding:
                                            "16px 0px 6px 4px !important",
                                        }
                                      : { padding: "10px 4px", height: "36px" },
                              },
                            },
                            error: error && error[field?.id],
                          }}
                        />
                      )}
                    </>
                  );
                })}
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default CollectionSummaryForm;
